<template>
  <div 
    class="under-construction-overlay"
    :style="{
      background: `linear-gradient(135deg, ${constructionSettings.construction_primary_color} 0%, ${constructionSettings.construction_accent_color} 100%)`
    }"
  >
    <div class="construction-container">
      <!-- Background Elements -->
      <div class="construction-bg">
        <div class="floating-shapes">
          <div class="shape shape-1"></div>
          <div class="shape shape-2"></div>
          <div class="shape shape-3"></div>
          <div class="shape shape-4"></div>
        </div>
      </div>

      <!-- Main Content -->
      <div 
        class="construction-content"
        :style="{
          backgroundColor: constructionSettings.construction_bg_color,
          color: constructionSettings.construction_text_color
        }"
      >
        <!-- Construction Icon with Animation -->
        <div class="construction-icon">
          <div 
            class="gear gear-1"
            :style="{ color: constructionSettings.construction_accent_color }"
          >
            <i class="bi bi-gear-fill"></i>
          </div>
          <div 
            class="gear gear-2"
            :style="{ color: constructionSettings.construction_primary_color }"
          >
            <i class="bi bi-gear-wide-connected"></i>
          </div>
        </div>

        <!-- Main Title -->
        <h1 
          class="construction-title"
          :style="{
            background: `linear-gradient(135deg, ${constructionSettings.construction_primary_color} 0%, ${constructionSettings.construction_accent_color} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }"
        >
          {{ constructionSettings.construction_title || 'Υπό Κατασκευή' }}
        </h1>

        <!-- Subtitle -->
        <p 
          class="construction-subtitle"
          :style="{ color: constructionSettings.construction_text_color }"
        >
          {{ constructionSettings.construction_subtitle || 'Εργαζόμαστε για να σας παρουσιάσουμε κάτι καταπληκτικό!' }}
        </p>

        <!-- Description -->
        <div 
          class="construction-description"
          :style="{ color: constructionSettings.construction_text_color, opacity: 0.8 }"
        >
          {{ constructionSettings.construction_description || 'Η ιστοσελίδα μας βρίσκεται προς το παρόν υπό κατασκευή. Παρακαλούμε επιστρέψτε σύντομα για να δείτε τις νέες βελτιώσεις.' }}
        </div>

        <!-- Progress Bar -->
        <div class="construction-progress">
          <div 
            class="progress-label"
            :style="{ color: constructionSettings.construction_text_color }"
          >
            Πρόοδος Εργασιών
          </div>
          <div class="progress-bar">
            <div 
              class="progress-fill"
              :style="{ 
                width: progressPercentage + '%',
                backgroundColor: constructionSettings.construction_accent_color || '#ff6b35'
              }"
            ></div>
          </div>
          <div 
            class="progress-text"
            :style="{ color: constructionSettings.construction_text_color, opacity: 0.7 }"
          >
            {{ progressPercentage }}% Ολοκληρώθηκε
          </div>
        </div>

        <!-- Contact Info -->
        <div v-if="constructionSettings.show_contact_info === 'true'" class="construction-contact">
          <div 
            class="contact-item"
            :style="{ color: constructionSettings.construction_text_color }"
          >
            <i 
              class="bi bi-envelope-fill"
              :style="{ color: constructionSettings.construction_primary_color }"
            ></i>
            <span>{{ constructionSettings.contact_email || 'info@example.com' }}</span>
          </div>
          <div 
            v-if="constructionSettings.show_contact_phone !== 'false'" 
            class="contact-item"
            :style="{ color: constructionSettings.construction_text_color }"
          >
            <i 
              class="bi bi-telephone-fill"
              :style="{ color: constructionSettings.construction_primary_color }"
            ></i>
            <span>{{ constructionSettings.contact_phone || '+30 210 1234567' }}</span>
          </div>
        </div>

        <!-- Admin Notice -->
        <div class="admin-notice">
          <i class="bi bi-info-circle-fill"></i>
          Αυτή η σελίδα είναι ορατή επειδή έχει ενεργοποιηθεί η λειτουργία "Υπό Κατασκευή" από το Admin Panel
        </div>
      </div>

      <!-- Footer -->
      <div class="construction-footer">
        <p>&copy; {{ new Date().getFullYear() }} {{ siteName }}. Όλα τα δικαιώματα διατηρούνται.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { API_BASE_URL } from '@/utils/config'
import { authFetch } from '@/utils/authFetch'

interface ConstructionSettings {
  under_construction_enabled: string
  construction_title: string
  construction_subtitle: string
  construction_description: string
  construction_primary_color: string
  construction_accent_color: string
  construction_bg_color: string
  construction_text_color: string
  show_contact_info: string
  show_contact_phone: string
  contact_email: string
  contact_phone: string
}

const constructionSettings = ref<ConstructionSettings>({
  under_construction_enabled: 'true',
  construction_title: 'Υπό Κατασκευή',
  construction_subtitle: 'Εργαζόμαστε για να σας παρουσιάσουμε κάτι καταπληκτικό!',
  construction_description: 'Η ιστοσελίδα μας βρίσκεται προς το παρόν υπό κατασκευή. Παρακαλούμε επιστρέψτε σύντομα για να δείτε τις νέες βελτιώσεις.',
  construction_primary_color: '#2c3e50',
  construction_accent_color: '#ff6b35',
  construction_bg_color: '#f8f9fa',
  construction_text_color: '#2c3e50',
  show_contact_info: 'true',
  show_contact_phone: 'true',
  contact_email: 'info@example.com',
  contact_phone: '+30 210 1234567'
})

const siteName = ref('StradaWebApp')
const progress = ref(0)
let progressInterval: ReturnType<typeof setInterval> | null = null

// Computed για το ποσοστό προόδου
const progressPercentage = computed(() => Math.round(progress.value))

// Φόρτωση ρυθμίσεων από API
const loadConstructionSettings = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/settings.php`)
    const data = await response.json()
    
    if (data.success && data.data.content) {
      const content = data.data.content
      Object.assign(constructionSettings.value, {
        under_construction_enabled: content.under_construction_enabled || 'true',
        construction_title: content.construction_title || 'Υπό Κατασκευή',
        construction_subtitle: content.construction_subtitle || 'Εργαζόμαστε για να σας παρουσιάσουμε κάτι καταπληκτικό!',
        construction_description: content.construction_description || 'Η ιστοσελίδα μας βρίσκεται προς το παρόν υπό κατασκευή. Παρακαλούμε επιστρέψτε σύντομα για να δείτε τις νέες βελτιώσεις.',
        construction_primary_color: content.construction_primary_color || '#2c3e50',
        construction_accent_color: content.construction_accent_color || '#ff6b35',
        construction_bg_color: content.construction_bg_color || '#f8f9fa',
        construction_text_color: content.construction_text_color || '#2c3e50',
        show_contact_info: content.show_contact_info || 'true',
        show_contact_phone: content.show_contact_phone || 'true',
        contact_email: content.contact_email || 'info@example.com',
        contact_phone: content.contact_phone || '+30 210 1234567'
      })
      
      if (content.company_name) {
        siteName.value = content.company_name
      }
    }
  } catch (error) {
  }
}

// Προσομοίωση προόδου
const startProgressAnimation = () => {
  progress.value = Math.random() * 30 + 65 // Random between 65-95%
  
  progressInterval = setInterval(() => {
    if (progress.value < 95) {
      progress.value += Math.random() * 2
    }
  }, 3000)
}

onMounted(async () => {
  await loadConstructionSettings()
  startProgressAnimation()
})

onUnmounted(() => {
  if (progressInterval) {
    clearInterval(progressInterval)
  }
})
</script>

<style scoped>
.under-construction-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Removed hardcoded gradient - now applied via inline style */
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-sans);
  overflow: auto;
}

.construction-container {
  max-width: 800px;
  width: 90%;
  position: relative;
  text-align: center;
  color: white;
  margin: 2rem 0;
}

/* Background Elements */
.construction-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
}

.floating-shapes {
  position: relative;
  width: 100%;
  height: 100%;
}

.shape {
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.shape-1 {
  width: 80px;
  height: 80px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.shape-2 {
  width: 60px;
  height: 60px;
  top: 20%;
  right: 15%;
  animation-delay: 2s;
}

.shape-3 {
  width: 100px;
  height: 100px;
  bottom: 20%;
  left: 20%;
  animation-delay: 4s;
}

.shape-4 {
  width: 40px;
  height: 40px;
  bottom: 15%;
  right: 10%;
  animation-delay: 1s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
    opacity: 1;
  }
}

/* Main Content */
.construction-content {
  position: relative;
  z-index: 2;
  /* Removed hardcoded background and color - now applied via inline style */
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 3rem 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

/* Construction Icon */
.construction-icon {
  position: relative;
  margin-bottom: 2rem;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gear {
  position: absolute;
  font-size: 4rem;
  /* Color now applied via inline style */
  animation: rotate 4s linear infinite;
}

.gear-1 {
  animation-direction: normal;
}

.gear-2 {
  font-size: 2.5rem;
  /* Color now applied via inline style */
  margin-left: 40px;
  margin-top: 20px;
  animation-direction: reverse;
  animation-duration: 3s;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Typography */
.construction-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  /* Gradient colors now applied via inline style */
}

.construction-subtitle {
  font-size: 1.3rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  /* Color now applied via inline style */
}

.construction-description {
  font-size: 1.1rem;
  line-height: 1.6;
  /* Color now applied via inline style */
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* Progress Bar */
.construction-progress {
  margin: 2rem 0;
}

.progress-label {
  font-size: 1rem;
  font-weight: 600;
  /* Color now applied via inline style */
  margin-bottom: 0.5rem;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 0.5rem;
  position: relative;
}

.progress-fill {
  height: 100%;
  /* Background color now applied via inline style */
  border-radius: 6px;
  transition: width 0.8s ease;
  position: relative;
  overflow: hidden;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.progress-text {
  font-size: 0.9rem;
  /* Color now applied via inline style */
}

/* Contact Info */
.construction-contact {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin: 2rem 0;
  flex-wrap: wrap;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  /* Color now applied via inline style */
}

.contact-item i {
  /* Color now applied via inline style */
  font-size: 1.2rem;
}

/* Admin Notice */
.admin-notice {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  color: #856404;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.admin-notice i {
  color: #f39c12;
}

/* Footer */
.construction-footer {
  position: relative;
  z-index: 2;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .construction-content {
    padding: 2rem 1.5rem;
  }
  
  .construction-title {
    font-size: 2.2rem;
  }
  
  .construction-subtitle {
    font-size: 1.1rem;
  }
  
  .construction-description {
    font-size: 1rem;
  }
  
  .gear {
    font-size: 3rem;
  }
  
  .gear-2 {
    font-size: 2rem;
  }
  
  .construction-contact {
    flex-direction: column;
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .construction-container {
    width: 95%;
    margin: 1rem 0;
  }
  
  .construction-content {
    padding: 1.5rem 1rem;
  }
  
  .construction-title {
    font-size: 1.8rem;
  }
  
  .construction-icon {
    height: 80px;
  }
  
  .gear {
    font-size: 2.5rem;
  }
  
  .gear-2 {
    font-size: 1.8rem;
    margin-left: 30px;
    margin-top: 15px;
  }
}
</style>